import { API_CONFIG, API_ENDPOINTS } from "@/config/api";
import { fetchApi, reportUnauthorized } from "./api";
import type {
  BakeJobSubmitResponse,
  BakeJobPollResponse,
  RegressionTestCase,
  ContextualScenePreset,
  ContextualJobStatus,
  NvgSimulationPresetInfo,
} from "@/types/nvgSimulation";
import type { ImageSize } from "@/types/nvgSimulation";

export async function listPresets(): Promise<
  Record<string, NvgSimulationPresetInfo>
> {
  return fetchApi<Record<string, NvgSimulationPresetInfo>>(
    API_ENDPOINTS.nvgPresets,
  );
}

export async function submitPresetBakeJob(
  name: string,
  imageSize?: ImageSize,
): Promise<BakeJobSubmitResponse> {
  const url = new URL(API_ENDPOINTS.nvgBakePreset(name));
  if (imageSize !== undefined) {
    url.searchParams.set("image_size", String(imageSize));
  }
  return fetchApi<BakeJobSubmitResponse>(url.toString(), {
    method: "GET",
  });
}

export async function submitRegressionBake(
  testCaseName: string,
  imageSize?: ImageSize,
): Promise<BakeJobSubmitResponse> {
  const url = new URL(API_ENDPOINTS.nvgRegressionBake(testCaseName));
  if (imageSize !== undefined) {
    url.searchParams.set("image_size", String(imageSize));
  }
  return fetchApi<BakeJobSubmitResponse>(url.toString(), {
    method: "GET",
  });
}

export async function pollBakeJob(jobId: string): Promise<BakeJobPollResponse> {
  return fetchApi<BakeJobPollResponse>(API_ENDPOINTS.nvgBakeStatus(jobId));
}

export async function fetchBakeAsset(assetPath: string): Promise<ArrayBuffer> {
  const response = await fetch(`${API_CONFIG.baseUrl}${assetPath}`, {
    credentials: "include",
  });

  if (!response.ok) {
    reportUnauthorized(assetPath, response.status);
    throw new Error(`Failed to fetch asset ${assetPath}: ${response.status}`);
  }

  return response.arrayBuffer();
}

export async function listRegressionTestCases(): Promise<RegressionTestCase[]> {
  return fetchApi<RegressionTestCase[]>(API_ENDPOINTS.nvgRegressionTestCases);
}

export function getRegressionBaselineUrl(
  name: string,
  type: "hoffman" | "cross_platform",
): string {
  return API_ENDPOINTS.nvgRegressionBaseline(name, type);
}

export async function listContextualPresets(): Promise<
  Record<string, ContextualScenePreset>
> {
  return fetchApi<Record<string, ContextualScenePreset>>(
    API_ENDPOINTS.nvgContextualPresets,
  );
}

export async function submitContextualPreset(
  presetName: string,
  tubePreset = "Gen3_Standard",
): Promise<BakeJobSubmitResponse> {
  return fetchApi<BakeJobSubmitResponse>(
    API_ENDPOINTS.nvgContextualPreset(presetName, tubePreset),
    {
      method: "GET",
    },
  );
}

export async function pollContextualJob(
  jobId: string,
): Promise<ContextualJobStatus> {
  return fetchApi<ContextualJobStatus>(
    API_ENDPOINTS.nvgContextualStatus(jobId),
  );
}

// Module-level blob URL cache. Keyed by request URL.
// Survives React component remounts and HMR updates to consuming modules,
// so images don't need to be re-fetched after code changes or tab switches.
const _blobCache = new Map<string, Promise<string>>();

function cachedBlobFetch(url: string): Promise<string> {
  if (!_blobCache.has(url)) {
    const p = fetch(url, { credentials: "include" })
      .then((r) => {
        if (!r.ok) {
          reportUnauthorized(url, r.status);
          throw new Error(`HTTP ${r.status} fetching ${url}`);
        }
        return r.blob();
      })
      .then((b) => URL.createObjectURL(b))
      .catch((e) => {
        _blobCache.delete(url); // don't cache failures
        throw e;
      });
    _blobCache.set(url, p);
  }
  return _blobCache.get(url)!;
}

export function fetchContextualAsset(
  jobId: string,
  assetName: "context" | "magnified",
): Promise<string> {
  return cachedBlobFetch(API_ENDPOINTS.nvgContextualAsset(jobId, assetName));
}

export function fetchContextualReveal(presetName: string): Promise<string> {
  return cachedBlobFetch(API_ENDPOINTS.nvgContextualReveal(presetName));
}

export function fetchContextualNightContext(
  presetName: string,
): Promise<string> {
  return cachedBlobFetch(API_ENDPOINTS.nvgContextualNightContext(presetName));
}

export function fetchContextualNightMagnified(
  presetName: string,
): Promise<string> {
  return cachedBlobFetch(API_ENDPOINTS.nvgContextualNightMagnified(presetName));
}
